import { useEffect, useState } from 'react';

import { addMonths, format, formatDistanceToNowStrict } from 'date-fns';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminOrganisationLayout } from './admin-organisation-layout';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import {
  ORG_PERMISSION_CONFIGS,
  ORG_PERMISSION_SECTIONS,
  OrgPermission,
  OrgPermissionConfig,
} from '../../../../../../../backend/src/organisation/organisation-permission';
import { useSetOrganisationPermission } from '@/api/mutations/useSetOrganisationPermission';
import { useUpdateOrganisationBlocking } from '@/api/mutations/useUpdateOrganisationBlocking';
import { useUpdateSlackChannel } from '@/api/mutations/useUpdateSlackChannel';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import type { Organisation } from '@/api/types/backend-dto-types';
import { useHasPermission } from '@/hooks/useHasPermission';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Alert, AlertDescription } from '@/new/components/shadcn/alert';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Switch } from '@/new/components/shadcn/switch';
import { BillingPlanHistory } from '@/new/pages/admin/organisations/_organisationId/billing-plan-history';
import { BillingPeriodSettings } from '@/new/pages/admin/organisations/_organisationId/current-billing-period';
import { CreditLimits } from '@/new/pages/organisation-settings/components/credit-limits';

function NewBillingPeriodLink({ organisationId }: { organisationId: string }) {
  return (
    <Link
      to={`/admin/organisations/${organisationId}/billing#new-billing-period`}
      className="inline-flex items-center gap-0.5 text-blue-500"
    >
      Add a new billing period now <Icon className="lucide--chevron-right" />
    </Link>
  );
}

function BillingPeriodAlert({
  organisation,
  canManageBilling,
}: {
  organisation: Organisation;
  canManageBilling: boolean;
}) {
  const currentPeriod = organisation.billingPeriods.current;
  const billingPermissionGuidance =
    'Please ask a platform admin with billing-management permission to add a new billing period.';
  const action = canManageBilling ? (
    <NewBillingPeriodLink organisationId={organisation.id} />
  ) : (
    billingPermissionGuidance
  );

  if (!currentPeriod) {
    const nextPeriod = organisation.billingPeriods.next;
    return (
      <Alert variant="destructive" className="mx-auto mb-7 max-w-3xl">
        <Icon className="lucide--circle-alert mt-0.75" />
        <div>
          <p>Action needed: No active billing period</p>
          <AlertDescription className="text-[13px]">
            <p>
              {nextPeriod
                ? `This organisation has no billing period covering today. Its next period starts on ${format(new Date(nextPeriod.startDate), 'd MMMM yyyy')}.`
                : 'This organisation has no billing period covering today. Billing periods can be scheduled with a start date in the future.'}{' '}
              {action}
            </p>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  const endDate = new Date(currentPeriod.endDate);
  const closesWithinTwoMonths = endDate <= addMonths(new Date(), 2);
  if (!closesWithinTwoMonths || organisation.billingPeriods.next) return null;

  return (
    <Alert variant="warning" className="mx-auto mb-7 max-w-3xl">
      <Icon className="lucide--circle-alert mt-0.75" />
      <div>
        <p>Action needed: Billing period ending soon</p>
        <AlertDescription className="text-[13px]">
          <p>
            The current billing period ends in {formatDistanceToNowStrict(endDate)}, and no next billing period is
            configured.{' '}
            {canManageBilling ? (
              <>
                You can schedule one by setting its start date in the future.{' '}
                <NewBillingPeriodLink organisationId={organisation.id} />
              </>
            ) : (
              billingPermissionGuidance
            )}
          </p>
        </AlertDescription>
      </div>
    </Alert>
  );
}

function PermissionRow({
  permission,
  isEnabled,
  onCheckedChange,
  disabled,
  isLoading = false,
}: {
  permission: Pick<OrgPermissionConfig, 'id' | 'title' | 'description'>;
  isEnabled: boolean;
  onCheckedChange: (enabled: boolean) => void;
  disabled?: boolean;
  isLoading?: boolean;
}) {
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  return (
    <SettingsRow title={permission.title} description={permission.description}>
      <Switch
        checked={isEnabled}
        disabled={disabled || isLoading}
        id={permission.id}
        onCheckedChange={(enabled) => setPendingEnabled(enabled)}
      />

      <ConfirmationModal
        isOpen={pendingEnabled !== null}
        onOpenChange={(open) => {
          if (!open) setPendingEnabled(null);
        }}
        title={pendingEnabled ? 'Enable permission' : 'Disable permission'}
        onConfirm={() => {
          if (pendingEnabled === null) return;
          onCheckedChange(pendingEnabled);
          setPendingEnabled(null);
        }}
        isLoading={isLoading}
      >
        <p>
          Are you sure you want to {pendingEnabled ? 'enable' : 'disable'} the{' '}
          <span className="font-semibold">{permission.title}</span> permission for this organisation?
        </p>
      </ConfirmationModal>
    </SettingsRow>
  );
}

export function AdminManageOrganisation({ section = 'configuration' }: { section?: 'configuration' | 'billing' }) {
  const { organisationId } = useParams();
  const location = useLocation();
  if (!organisationId) return null;

  usePlatformPermissionCheck([Permission.PlatformManageOrganisations, Permission.PlatformManageBilling]);
  const { hasPermission } = useHasPermission();
  const canManageOrganisation = hasPermission(Permission.PlatformManageOrganisations);
  const canManageBilling = hasPermission(Permission.PlatformManageBilling);

  const { data: organisation } = useGetOrganisation(organisationId);

  useEffect(() => {
    if (section !== 'billing' || location.hash !== '#new-billing-period' || !organisation) return;

    const frame = requestAnimationFrame(() => {
      document.getElementById('new-billing-period')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash, organisation, section]);

  const { mutate: updateBlocking } = useUpdateOrganisationBlocking(organisationId);
  const { mutate: setOrganisationPermission, isLoading: isSettingPermission } =
    useSetOrganisationPermission(organisationId);
  const { mutate: updateSlackChannel, isLoading: isUpdatingSlackChannel } = useUpdateSlackChannel(organisationId);

  const [slackChannelId, setSlackChannelId] = useState<string>('');

  function setPermission(permission: OrgPermission, enabled: boolean) {
    if (!organisation) return;

    setOrganisationPermission({ permission, enabled });
  }

  useEffect(() => {
    setSlackChannelId(organisation?.slackChannelId ?? '');
  }, [organisation]);

  if (section === 'configuration' && !canManageOrganisation && canManageBilling) {
    return <Navigate to={`/admin/organisations/${organisationId}/billing`} replace />;
  }

  return (
    <AdminOrganisationLayout>
      {organisation && (
        <>
          {section === 'configuration' && canManageOrganisation && (
            <SettingsContent>
              <BillingPeriodAlert organisation={organisation} canManageBilling={canManageBilling} />

              {ORG_PERMISSION_SECTIONS.map((permissionSection) => {
                const configs = ORG_PERMISSION_CONFIGS.filter((config) => config.kind === permissionSection.kind);
                return (
                  <SettingsSection
                    key={permissionSection.kind}
                    title={permissionSection.title}
                    description={permissionSection.description}
                  >
                    {configs.length === 0 ? (
                      <SettingsRow title={permissionSection.emptyText} />
                    ) : (
                      configs.map((permission) => (
                        <PermissionRow
                          key={permission.id}
                          permission={permission}
                          isEnabled={organisation.permissions.includes(permission.permission)}
                          onCheckedChange={(enabled) => setPermission(permission.permission, enabled)}
                          isLoading={isSettingPermission}
                        />
                      ))
                    )}
                  </SettingsSection>
                );
              })}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateSlackChannel(
                    { slackChannelId: slackChannelId.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Slack channel updated');
                      },
                    },
                  );
                }}
              >
                <SettingsSection
                  title="Slack channel ID"
                  footer={
                    <SettingsSectionFooter className="justify-end">
                      <Button
                        type="submit"
                        disabled={isUpdatingSlackChannel || slackChannelId === (organisation?.slackChannelId ?? '')}
                      >
                        Update Slack Channel
                      </Button>
                    </SettingsSectionFooter>
                  }
                >
                  <SettingsRow
                    title="Wexler Slack Channel ID"
                    description="Organisation-specific run alerts are sent here. Leave empty to disable these alerts; generic Run Added messages still go to the monitoring channel."
                  >
                    <Input
                      className="w-64"
                      type="text"
                      id="slack-channel-id"
                      placeholder="e.g. C0123456789"
                      value={slackChannelId}
                      onChange={(e) => setSlackChannelId(e.target.value)}
                      disabled={isUpdatingSlackChannel}
                    />
                  </SettingsRow>
                </SettingsSection>
              </form>
            </SettingsContent>
          )}

          {section === 'billing' && canManageBilling && (
            <SettingsContent>
              <BillingPeriodAlert organisation={organisation} canManageBilling={canManageBilling} />

              <CreditLimits
                organisation={organisation}
                organisationId={organisationId}
                canManage
                includeContentWrapper={false}
                showReviewerInstructions={false}
              />

              <SettingsSection title="Usage blocking">
                <PermissionRow
                  permission={{
                    id: 'blocking-toggle',
                    title: 'Blocking Enabled',
                    description:
                      'Recommended. When enabled, users/organisations who exceed their usage limits will be blocked from further uploads and runs. Kim usage is never blocked.',
                  }}
                  isEnabled={organisation.isBlockingEnabled}
                  onCheckedChange={() =>
                    updateBlocking(
                      {
                        isBlockingEnabled: !organisation.isBlockingEnabled,
                      },
                      {
                        onSuccess: () => {
                          toast.success(
                            `Blocking ${organisation.isBlockingEnabled ? 'disabled' : 'enabled'} for this organisation`,
                          );
                        },
                      },
                    )
                  }
                />
              </SettingsSection>

              <BillingPeriodSettings organisationId={organisationId} />

              <BillingPlanHistory key={organisationId} organisationId={organisationId} />
            </SettingsContent>
          )}
        </>
      )}
    </AdminOrganisationLayout>
  );
}
